/**
 * Every tier, in escalating-consequence order: hard blocks everything,
 * information blocks nothing. The gate of task 5.5 reads them in this order.
 */
export const VALIDATION_TIERS = Object.freeze([
    'hard',
    'governance',
    'warning',
    'information',
]);
/** The three tiers configuration may name (R8.9). */
export const CONFIGURABLE_VALIDATION_TIERS = Object.freeze([
    'governance',
    'warning',
    'information',
]);
/** Whether a tier is one configuration may set (R8.9). */
export function isConfigurableValidationTier(tier) {
    return tier !== 'hard';
}
/**
 * The field paths the registered rules report.
 *
 * Named rather than inlined because task 5.9 has to derive `FieldVisibility` over
 * the same paths - R5.3 requires a hidden field to be excluded from validation,
 * which is only checkable if both sides address fields by the same names.
 */
export const INITIATIVE_FIELD_PATHS = Object.freeze({
    title: 'title',
    ownerUserId: 'ownerUserId',
    backupOwnerUserId: 'backupOwnerUserId',
    startDate: 'startDate',
    endDate: 'endDate',
    platforms: 'platforms',
    platformSplit: 'platforms.splitPercent',
    channelIds: 'channelIds',
    customerGroupIds: 'customerGroupIds',
    audienceIds: 'audienceIds',
    categoryIds: 'categoryIds',
    initiativeTypeIds: 'initiativeTypeIds',
    cscAttributeIds: 'cscAttributeIds',
    priorityId: 'priorityId',
    salesCycleIds: 'salesCycleIds',
    milestones: 'milestones',
    dependencies: 'dependencies',
    fieldForceScope: 'fieldForceScope',
    isConfidential: 'isConfidential',
});
/**
 * Every validation rule identifier this release knows, in evaluation order:
 * the hard rules, then governance, then warning, then information.
 *
 * **This list is the committed identifier snapshot of R8.1 and its members are
 * append-only.** A finding is reported by identifier, an acknowledgement is
 * stored against one (`warning_acknowledgement.rule_id`) and a configuration row
 * is keyed by one (`validation_rule_config.rule_id`), all as bare text with no
 * foreign key to fall back on. Renumbering `WV-002` would silently orphan every
 * acknowledgement stored against it, and re-using a retired identifier for a
 * different rule would silently satisfy them. A withdrawn rule therefore leaves
 * its identifier behind rather than closing the gap.
 *
 * The prefixes record the tier a rule was *declared* in, which is the only tier
 * a hard rule ever has. A configurable rule's effective tier comes from
 * configuration and can differ from its prefix; the prefix is part of the
 * identifier, so it does not follow.
 *
 * The union {@link ValidationRuleId} is derived from this array, and the registry
 * is a total mapping over that union. Adding an identifier here without
 * implementing it, or implementing a rule without listing it here, is a compile
 * error rather than a silent gap in the rule set.
 */
const VALIDATION_RULE_ID_LIST = [
    // The six non-configurable hard rules of R8.3, in the order the criterion
    // names them, plus the Platform set structure rule of R4.4.
    'HV-001',
    'HV-002',
    'HV-003',
    'HV-004',
    'HV-005',
    'HV-006',
    'HV-007',
    // The Foundation governance rules of R8.10, in the order the criterion
    // names them, plus the inactive-owner rule of R4.11. `GV-006` (dependency
    // owner or due date missing) is retired: both fields are optional, so the
    // identifier is left unused rather than reassigned.
    'GV-001',
    'GV-002',
    'GV-003',
    'GV-004',
    'GV-005',
    'GV-007',
    // The two warning rules of R8.6.
    'WV-001',
    'WV-002',
    // The information-tier context of R8.7.
    'IV-001',
    'IV-002',
];
/** Every rule identifier, in evaluation order. */
export const VALIDATION_RULE_IDS = Object.freeze(VALIDATION_RULE_ID_LIST);
/** Whether a string is a rule identifier this release knows. */
export function isValidationRuleId(value) {
    return VALIDATION_RULE_IDS.includes(value);
}
/**
 * The six hard rules R8.3 enumerates, in its order: empty or over-length Title,
 * missing Owner, end before start, inactive controlled value, Platform split not
 * totalling 100, unauthorized confidentiality change.
 *
 * `HV-007` is deliberately not a member. It carries the Platform set *structure*
 * rules of R4.4 - one primary, no repeats, at most ten members - which are hard
 * because a set violating them cannot be stored, but they are not among the six
 * R8.3 names. Keeping the two apart is what lets a reader check the registry
 * against the criterion by counting.
 */
export const R8_3_HARD_RULE_IDS = Object.freeze([
    'HV-001',
    'HV-002',
    'HV-003',
    'HV-004',
    'HV-005',
    'HV-006',
]);
/**
 * The five Foundation governance rules of R8.10, in its order.
 *
 * `GV-007` is deliberately not a member: it is the inactive-owner governance
 * finding of R4.11, which R8.10 does not list. `GV-006` is retired: the DTE and
 * CSC dependency owner and due date are optional.
 */
export const R8_10_GOVERNANCE_RULE_IDS = Object.freeze([
    'GV-001',
    'GV-002',
    'GV-003',
    'GV-004',
    'GV-005',
]);
/** The two warning rules of R8.6. */
export const R8_6_WARNING_RULE_IDS = Object.freeze([
    'WV-001',
    'WV-002',
]);
/** The information-tier findings R8.7 names. */
export const R8_7_INFORMATION_RULE_IDS = Object.freeze([
    'IV-001',
    'IV-002',
]);
/** Shortest configured message R8.9 accepts. */
export const MIN_RULE_MESSAGE_LENGTH = 1;
/** Longest configured message R8.9 accepts. */
export const MAX_RULE_MESSAGE_LENGTH = 500;
/**
 * Whether a configured message is within the 1 to 500 characters R8.9 allows,
 * mirroring `validation_rule_config`'s `check (length(message) between 1 and 500)`.
 */
export function isValidRuleMessage(message) {
    return (message.length >= MIN_RULE_MESSAGE_LENGTH && message.length <= MAX_RULE_MESSAGE_LENGTH);
}
/** No rule configured: every rule active, in its declared tier, with its own key. */
export const EMPTY_VALIDATION_CONFIGURATION = Object.freeze(new Map());
/**
 * Resolves a rule's effective settings against the configuration overlay (R8.9).
 *
 * A non-configurable rule short-circuits: it never reads the overlay, so a hard
 * rule cannot be deactivated, retiered or given a different message however the
 * overlay was built. `validationConfiguration` already drops such rows; the check
 * is repeated here because this function is also the one a caller reaches for
 * directly, and the guarantee should not depend on which door was used.
 */
export function effectiveRuleSettings(rule, configuration) {
    const configured = rule.configurable ? configuration.get(rule.id) : undefined;
    if (configured === undefined) {
        return {
            ruleId: rule.id,
            tier: rule.declaredTier,
            messageOverride: null,
            isActive: true,
            configurationApplied: false,
        };
    }
    return {
        ruleId: rule.id,
        tier: configured.tier,
        messageOverride: configured.message,
        isActive: configured.isActive,
        configurationApplied: true,
    };
}
//# sourceMappingURL=validation-rule.js.map